import { FormLoja } from '@/models'
import { ASSUNTO_POR_ID, IdDoAssunto } from './catalogo-de-parametros'

export type IdDaSecao =
	| 'dados'
	| 'funcionarios'
	| 'endereco'
	| 'marcas'
	| 'clientes'
	| 'lojas'
	| 'fiscal'
	| 'emissao'
	| 'caixa'
	| 'pdvs'
	| 'venda'
	| 'pagamento'
	| 'trocas'
	| 'tabelas'
	| 'prevenda'
	| 'estoque'
	| 'entrada'
	| 'pedidos'
	| 'crediario'
	| 'email'
	| 'nps'

export interface SecaoDaLoja {
	id: IdDaSecao
	titulo: string
	icone: string
	resumo: string
	/** Onde o conteúdo ficava na tela antiga. */
	abaAntiga: string
	/** Os parâmetros do catálogo que a seção mostra. */
	assunto?: IdDoAssunto
	avisoCaixa?: boolean
}

export interface GrupoDeSecoes {
	titulo: string
	secoes: SecaoDaLoja[]
}

const doAssunto = (assunto: IdDoAssunto, titulo: string, abaAntiga: string): SecaoDaLoja => ({
	id: assunto,
	assunto,
	titulo,
	icone: ASSUNTO_POR_ID[assunto].icone,
	resumo: ASSUNTO_POR_ID[assunto].resumo,
	avisoCaixa: ASSUNTO_POR_ID[assunto].avisoCaixa,
	abaAntiga,
})

export const GRUPOS_DE_SECOES: GrupoDeSecoes[] = [
	{
		titulo: 'Cadastro',
		secoes: [
			{ id: 'dados', titulo: 'Dados da loja', icone: 'mdi-store-outline', resumo: 'Nome, CNPJ, grupo econômico e identificação da loja.', abaAntiga: 'Geral' },
			{ id: 'funcionarios', titulo: 'Funcionários', icone: 'mdi-account-group-outline', resumo: 'Quem trabalha nesta loja.', abaAntiga: 'Geral' },
			{ id: 'endereco', titulo: 'Endereço e contato', icone: 'mdi-map-marker-outline', resumo: 'Onde a loja fica e os telefones dela.', abaAntiga: 'Endereço e Contato' },
			{ id: 'marcas', titulo: 'Marcas', icone: 'mdi-tag-outline', resumo: 'Marcas que a loja trabalha.', abaAntiga: 'Marcas' },
			{ id: 'clientes', titulo: 'Clientes da loja', icone: 'mdi-account-multiple-outline', resumo: 'Clientes vinculados a esta loja.', abaAntiga: 'Clientes' },
			{ id: 'lojas', titulo: 'Lojas vinculadas', icone: 'mdi-link-variant', resumo: 'Outras lojas ligadas a esta.', abaAntiga: 'Lojas' },
		],
	},
	{
		titulo: 'Fiscal',
		secoes: [
			{ id: 'fiscal', titulo: 'Dados fiscais e certificado', icone: 'mdi-certificate-outline', resumo: 'Inscrições, regime, CSC e certificado digital.', abaAntiga: 'Fiscal' },
			{
				...doAssunto('emissao', 'Emissão de notas', 'Outros e Fiscal'),
				resumo: 'Séries das notas, validações ao emitir, venda para outro estado, o que vai no XML e na DANFE e os dados do Procon.',
			},
		],
	},
	{
		titulo: 'Vendas e caixa',
		secoes: [
			doAssunto('caixa', 'Caixa', 'Outros'),
			{ id: 'pdvs', titulo: 'Pontos de venda', icone: 'mdi-point-of-sale', resumo: 'Os PDVs da loja e o que cada um usa.', abaAntiga: 'Geral' },
			doAssunto('venda', 'Vendas no caixa', 'Outros'),
			{
				...doAssunto('pagamento', 'Pagamentos e TEF', 'Pagamentos e Outros'),
				resumo: 'Formas de pagamento da loja, regras de pagamento no caixa e a integração com a maquininha.',
			},
			{
				...doAssunto('trocas', 'Trocas e devoluções', 'Outros e Fiscal'),
				resumo: 'Prazo e regras da troca, o voucher que sobra para o cliente, o vale-troca e a política de troca impressa.',
			},
			{ id: 'tabelas', titulo: 'Tabelas de preços', icone: 'mdi-table-large', resumo: 'Tabelas de preço que a loja usa, por tipo de cliente.', abaAntiga: 'Tabelas de Preços' },
			doAssunto('prevenda', 'Pré-venda e romaneio', 'Outros e Romaneios'),
		],
	},
	{
		titulo: 'Retaguarda',
		secoes: [
			doAssunto('estoque', 'Estoque', 'Outros'),
			doAssunto('entrada', 'Entrada de notas', 'Outros'),
			doAssunto('pedidos', 'Pedidos e entregas', 'Outros'),
		],
	},
	{
		titulo: 'Crédito',
		secoes: [doAssunto('crediario', 'Crediário e cobrança', 'Outros')],
	},
	{
		titulo: 'Comunicação',
		secoes: [
			{ id: 'email', titulo: 'E-mail e notificações', icone: 'mdi-email-outline', resumo: 'E-mails automáticos para o cliente, o servidor de e-mail da loja e as notificações da equipe.', abaAntiga: 'Envio de e-mail e Notificações' },
			{ id: 'nps', titulo: 'Avaliação NPS', icone: 'mdi-star-outline', resumo: 'Pesquisa de satisfação depois da venda.', abaAntiga: 'Avaliação NPS' },
		],
	},
]

export const SECOES_DA_LOJA: SecaoDaLoja[] = GRUPOS_DE_SECOES.reduce(
	(secoes, grupo) => secoes.concat(grupo.secoes),
	[] as SecaoDaLoja[],
)

export const SECAO_POR_ID = SECOES_DA_LOJA.reduce(
	(mapa, secao) => ({ ...mapa, [secao.id]: secao }),
	{} as Record<IdDaSecao, SecaoDaLoja>,
)

/** Para links antigos com ?t=<aba>: a seção onde o começo daquela aba foi parar. */
export const SECAO_DA_ABA_ANTIGA: Record<string, IdDaSecao> = {
	'Geral': 'dados',
	'Endereço e Contato': 'endereco',
	'Fiscal': 'fiscal',
	'Pagamentos': 'pagamento',
	'Tabelas de Preços': 'tabelas',
	'Marcas': 'marcas',
	'Clientes': 'clientes',
	'Lojas': 'lojas',
	'Envio de e-mail': 'email',
	'Notificações': 'email',
	'Avaliação NPS': 'nps',
	'Romaneios': 'prevenda',
	'Outros': 'caixa',
}

/** O caminho inverso, para quem usa a tela antiga e abre um link da nova. */
export const ABA_ANTIGA_DA_SECAO: Record<IdDaSecao, string> = {
	dados: 'Geral',
	funcionarios: 'Geral',
	endereco: 'Endereço e Contato',
	marcas: 'Marcas',
	clientes: 'Clientes',
	lojas: 'Lojas',
	fiscal: 'Fiscal',
	emissao: 'Outros',
	caixa: 'Outros',
	pdvs: 'Geral',
	venda: 'Outros',
	pagamento: 'Outros',
	trocas: 'Outros',
	tabelas: 'Tabelas de Preços',
	prevenda: 'Outros',
	estoque: 'Outros',
	entrada: 'Outros',
	pedidos: 'Outros',
	crediario: 'Outros',
	email: 'Envio de e-mail',
	nps: 'Avaliação NPS',
}

/**
 * Campos da loja que não são parâmetros do catálogo: entram na busca ("Abrir" leva à seção) e na contagem de
 * alterações. Sem `valor`, o campo só entra na busca (as listas que gravam sozinhas, pela API).
 */
export interface CampoDeCadastro {
	id: string
	titulo: string
	secao: IdDaSecao
	busca: string[]
	valor?: (loja: FormLoja) => unknown
}

const ids = (lista: { id: string }[] | null | undefined) => (lista || []).map(({ id }) => id).sort()
const soDigitos = (valor: string | null | undefined) => (valor || '').replace(/\D/g, '')
// A tabela de formas de pagamento ordena a lista da loja no lugar ao desenhar; a ordem que vale é o campo ordem
const porId = <T extends { id?: string | null }>(lista: T[] | null | undefined) =>
	[...(lista || [])].sort((a, b) => String(a.id || '').localeCompare(String(b.id || '')))

export const CAMPOS_DE_CADASTRO: CampoDeCadastro[] = [
	{ id: 'nomeFantasia', titulo: 'Nome fantasia', secao: 'dados', busca: ['nome da loja', 'fantasia'], valor: loja => loja.nomeFantasia },
	{ id: 'razaoSocial', titulo: 'Razão social', secao: 'dados', busca: ['razao social', 'nome empresarial'], valor: loja => loja.razaoSocial },
	{ id: 'cnpj', titulo: 'CNPJ', secao: 'dados', busca: ['cnpj', 'documento'], valor: loja => soDigitos(loja.cnpj) },
	{ id: 'email', titulo: 'E-mail da loja', secao: 'dados', busca: ['email', 'e-mail'], valor: loja => loja.email },
	{ id: 'grupoEconomico', titulo: 'Grupo econômico', secao: 'dados', busca: ['grupo economico', 'rede', 'matriz'], valor: loja => loja.grupoEconomico ? loja.grupoEconomico.id : null },
	{ id: 'subdominio', titulo: 'Subdomínio', secao: 'dados', busca: ['subdominio', 'endereco do site'], valor: loja => loja.subdominio },
	{ id: 'inativarLoja', titulo: 'Inativar loja', secao: 'dados', busca: ['inativar', 'desativar', 'loja fechada'], valor: loja => !!loja.inativarLoja },
	{ id: 'excluirLoja', titulo: 'Excluir a loja', secao: 'dados', busca: ['excluir', 'apagar', 'remover loja'] },
	{ id: 'usuarios', titulo: 'Funcionários', secao: 'funcionarios', busca: ['funcionario', 'colaboradores', 'quadro de colaboradores', 'usuarios', 'equipe', 'vendedores'], valor: loja => ids(loja.usuarios) },
	{ id: 'endereco', titulo: 'Endereço', secao: 'endereco', busca: ['endereco', 'cep', 'rua', 'bairro', 'cidade', 'uf', 'logradouro'], valor: loja => loja.endereco },
	{ id: 'telefones', titulo: 'Telefones', secao: 'endereco', busca: ['telefone', 'fone', 'contato', 'celular'], valor: loja => loja.telefones },
	{ id: 'marcas', titulo: 'Marcas da loja', secao: 'marcas', busca: ['marca', 'grife'], valor: loja => ids(loja.marcas) },
	{ id: 'clientes', titulo: 'Clientes vinculados', secao: 'clientes', busca: ['cliente', 'vincular cliente'] },
	{ id: 'lojasVinculadas', titulo: 'Lojas vinculadas', secao: 'lojas', busca: ['loja vinculada', 'vincular loja', 'outras lojas'] },
	{ id: 'inscricaoEstadual', titulo: 'Inscrição estadual', secao: 'fiscal', busca: ['ie', 'inscricao estadual', 'isento'], valor: loja => loja.inscricaoEstadual },
	{ id: 'inscricaoMunicipal', titulo: 'Inscrição municipal', secao: 'fiscal', busca: ['im', 'inscricao municipal'], valor: loja => loja.inscricaoMunicipal },
	{ id: 'cfopPadrao', titulo: 'CFOP padrão', secao: 'fiscal', busca: ['cfop', 'natureza da operacao'], valor: loja => loja.cfopPadrao },
	{ id: 'regimeFiscal', titulo: 'Regime fiscal da loja', secao: 'fiscal', busca: ['regime', 'simples nacional', 'lucro presumido', 'crt'], valor: loja => loja.regimeFiscal },
	{ id: 'campoAdicional', titulo: 'Informações adicionais da nota fiscal', secao: 'fiscal', busca: ['informacoes adicionais', 'observacao da nota', 'infcpl'], valor: loja => loja.campoAdicional },
	{ id: 'idDoContribuinte', titulo: 'ID do contribuinte', secao: 'fiscal', busca: ['id do csc', 'id token', 'nfce'], valor: loja => loja.configuracaoDaLoja.idDoContribuinte },
	{ id: 'codigoSegurancaContribuinte', titulo: 'Código de segurança do contribuinte (CSC)', secao: 'fiscal', busca: ['csc', 'token', 'nfce', 'qr code'], valor: loja => loja.configuracaoDaLoja.codigoSegurancaContribuinte },
	{ id: 'codigoCSRT', titulo: 'Código de segurança do responsável técnico (CSRT)', secao: 'fiscal', busca: ['csrt', 'responsavel tecnico'], valor: loja => loja.configuracaoDaLoja.codigoCSRT },
	{ id: 'certificado', titulo: 'Certificado NF-e', secao: 'fiscal', busca: ['certificado digital', 'a1', 'pfx', 'senha do certificado'], valor: loja => [loja.urlCertificadoNfe, loja.senhaCertificadoNfe || null] },
	{ id: 'seriesFiscais', titulo: 'Séries das notas', secao: 'emissao', busca: ['serie', 'numeracao', 'nfce', 'nfe', 'numero da nota'], valor: loja => loja.seriesFiscais },
	{
		id: 'procon', titulo: 'Dados do Procon', secao: 'emissao', busca: ['procon', 'defesa do consumidor', 'orgao'],
		valor: ({ configuracaoDaLoja: c }) => [c.proconHabilitado, c.proconNomeOrgao, c.proconTelefone, c.proconEndereco, c.proconCep],
	},
	{ id: 'tiposDePagamento', titulo: 'Formas de pagamento', secao: 'pagamento', busca: ['forma de pagamento', 'dinheiro', 'cartao', 'pix', 'parcelas', 'bandeira'], valor: loja => porId(loja.tiposDePagamento) },
	{ id: 'copiarFormasDePagamento', titulo: 'Copiar formas de pagamento de outra loja', secao: 'pagamento', busca: ['importar dados de outra loja', 'copiar', 'importar'] },
	{
		id: 'politicaDeTroca', titulo: 'Política de troca impressa na NFC-e', secao: 'trocas', busca: ['politica de troca', 'texto da troca', 'cupom'],
		valor: ({ configuracaoDaLoja: c }) => [c.politicaDeTrocaHabilitada, c.politicaDeTrocaTexto],
	},
	{ id: 'tabelasDePreco', titulo: 'Tabelas de preços da loja', secao: 'tabelas', busca: ['tabela de preco', 'tipo de cliente', 'atacado', 'varejo'], valor: loja => loja.configuracaoDaLoja.tabelasDePreco },
	{ id: 'pdvs', titulo: 'Pontos de venda', secao: 'pdvs', busca: ['pdv', 'caixa', 'terminal', 'ponto de venda'], valor: loja => loja.pdvs },
	{ id: 'comandas', titulo: 'Romaneios', secao: 'prevenda', busca: ['romaneio', 'comanda'], valor: loja => loja.comandas || [] },
	{ id: 'emailsAutomaticos', titulo: 'E-mails automáticos para o cliente', secao: 'email', busca: ['envio de email', 'email automatico', 'nota por email'] },
	{
		id: 'servidorDeEmail', titulo: 'Servidor de e-mail da loja', secao: 'email', busca: ['smtp', 'servidor de email', 'host', 'porta', 'notificacoes por email'],
		valor: ({ configuracaoDaLoja: c }) => [c.permiteConfiguracaoDeNotificacoes, c.smtpHost, c.smtpPort, c.smtpUsuario, c.smtpSenha || null],
	},
	{ id: 'notificacoesDaEquipe', titulo: 'Notificações da equipe', secao: 'email', busca: ['notificacao', 'aviso', 'nota em transito', 'estoque'] },
	{
		id: 'avaliacaoNps', titulo: 'Avaliação NPS', secao: 'nps', busca: ['nps', 'pesquisa de satisfacao', 'avaliacao', 'perguntas'],
		valor: loja => [loja.configuracaoDaLoja.utilizarAvaliacaoNps, loja.configuracaoDaLoja.diasExpiracaoDaAvaliacaoNps, loja.avaliacaoNpsConfig],
	},
]
